import React, { useCallback, useState } from 'react';
import { Formik, FormikProps, prepareDataForValidation } from 'formik';
import { Form } from 'formik-antd';
import { Col, Row } from 'antd';
import * as Yup from 'yup';
import { isNil } from 'lodash';
import { RadioChangeEvent } from 'antd/lib/radio/interface';

import Radio from 'components/formik/Radio';
import FormItem from 'components/formik/FormItem';
import Input from 'components/formik/Input';
import AgreementUploader from './AgreementUploader';
import FormikEffect from 'components/formik/FormikEffect';
import { ServiceAgreementTypeEnum } from 'api/backofficeApiClient';
import {
  getAgreementFieldName,
  isFileUploaderRequired,
} from 'utils/clientUtils';
import Checkbox from 'components/formik/Checkbox';
import { BaseFileYupObject } from 'components/formik/FormikFileUpload';
import { normalCharactersLength } from 'constants/index';

import style from './ServiceAgreement.module.less';
import { AgreementFileValue } from './AgreementUploader/AgreementUploader';

export interface ServiceAgreementFormValues {
  serviceAgreement: ServiceAgreementTypeEnum;
  clientAgreement?: AgreementFileValue[];
  memberAgreement?: AgreementFileValue[];
  myBasePayAgreement?: AgreementFileValue[];
  isAgreementNotAvailableConfirmed?: boolean;
  contractSignerFirstName?: string;
  contractSignerLastName?: string;
  contractSignerPhone?: string;
  contractSignerEmail?: string;
  isContractSignerCompleted?: boolean;
}

export const isServiceAgreementValidForDraft = (
  formik: FormikProps<ServiceAgreementFormValues>,
) => {
  const dataForValidation = prepareDataForValidation(formik.values);

  return draftValidationSchema.isValidSync(dataForValidation);
};

type Props = {
  formRef: any;
  onChange: (formik: FormikProps<ServiceAgreementFormValues>) => void;
  initialValues?: ServiceAgreementFormValues;
  isAdmin?: boolean;
};

const initialValidationSchema = {
  serviceAgreement: Yup.number(),
  clientAgreement: Yup.array(),
  myBasePayAgreement: Yup.array(),
  memberAgreement: Yup.array(),
  isAgreementNotAvailableConfirmed: Yup.boolean(),
  contractSignerFirstName: Yup.string()
    .trim()
    .max(
      normalCharactersLength,
      `First name should be ${normalCharactersLength} characters maximum`,
    ),
  contractSignerLastName: Yup.string()
    .trim()
    .max(
      normalCharactersLength,
      `Last name should be ${normalCharactersLength} characters maximum`,
    ),
  contractSignerPhone: Yup.string()
    .trim()
    .max(
      normalCharactersLength,
      `Phone number should be ${normalCharactersLength} characters maximum`,
    ),
  contractSignerEmail: Yup.string()
    .trim()
    .max(
      normalCharactersLength,
      `Email should be ${normalCharactersLength} characters maximum`,
    )
    .test(
      'email-format',
      'Email format is invalid',
      (value) => !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
    ),
};

const isContractSignerSectionActive = (
  values: ServiceAgreementFormValues | undefined,
): boolean => {
  if (!values?.serviceAgreement) {
    return false;
  }

  if (!isFileUploaderRequired(values.serviceAgreement)) {
    return false;
  }

  const agreementField = getAgreementFieldName(values.serviceAgreement);
  if (!agreementField) {
    return false;
  }

  const uploadedFiles = values[
    agreementField as keyof ServiceAgreementFormValues
  ] as AgreementFileValue[] | undefined;

  return !!uploadedFiles?.length;
};

const draftValidationSchema = Yup.object(initialValidationSchema);
const submitValidationSchema = Yup.object({
  ...initialValidationSchema,
  contractSignerFirstName: initialValidationSchema.contractSignerFirstName.test(
    'required-when-active',
    'Field is required',
    function (value) {
      if (
        !isContractSignerSectionActive(
          this.parent as ServiceAgreementFormValues,
        )
      ) {
        return true;
      }
      return !!value?.trim();
    },
  ),
  contractSignerLastName: initialValidationSchema.contractSignerLastName.test(
    'required-when-active',
    'Field is required',
    function (value) {
      if (
        !isContractSignerSectionActive(
          this.parent as ServiceAgreementFormValues,
        )
      ) {
        return true;
      }
      return !!value?.trim();
    },
  ),
  contractSignerPhone: initialValidationSchema.contractSignerPhone.test(
    'required-when-active',
    'Field is required',
    function (value) {
      if (
        !isContractSignerSectionActive(
          this.parent as ServiceAgreementFormValues,
        )
      ) {
        return true;
      }
      return !!value?.trim();
    },
  ),
  contractSignerEmail: initialValidationSchema.contractSignerEmail.test(
    'required-when-active',
    'Field is required',
    function (value) {
      if (
        !isContractSignerSectionActive(
          this.parent as ServiceAgreementFormValues,
        )
      ) {
        return true;
      }
      return !!value?.trim();
    },
  ),
  clientAgreement: initialValidationSchema.clientAgreement.when(
    'serviceAgreement',
    {
      is: (agreement: ServiceAgreementTypeEnum) =>
        agreement === ServiceAgreementTypeEnum.ClientAgreement,
      then: Yup.array().of(BaseFileYupObject).min(1, 'Field is required'),
    },
  ),
  myBasePayAgreement: initialValidationSchema.myBasePayAgreement.when(
    'serviceAgreement',
    {
      is: (agreement: ServiceAgreementTypeEnum) =>
        agreement === ServiceAgreementTypeEnum.MyBasePayAgreement,
      then: Yup.array().of(BaseFileYupObject).min(1, 'Field is required'),
    },
  ),
  memberAgreement: initialValidationSchema.memberAgreement.when(
    'serviceAgreement',
    {
      is: (agreement: ServiceAgreementTypeEnum) =>
        agreement === ServiceAgreementTypeEnum.MemberAgreement,
      then: Yup.array().of(BaseFileYupObject).min(1, 'Field is required'),
    },
  ),
  isAgreementNotAvailableConfirmed:
    initialValidationSchema.isAgreementNotAvailableConfirmed.when(
      'serviceAgreement',
      {
        is: (agreement: ServiceAgreementTypeEnum) =>
          agreement === ServiceAgreementTypeEnum.NotAvailable,
        then: Yup.boolean()
          .oneOf([true], 'Field is required')
          .required('Field is required'),
      },
    ),
});

export default function ServiceAgreement(props: Props) {
  const {
    formRef,
    onChange,
    initialValues: predefinedInitialValues,
    isAdmin,
  } = props;
  const [agreementDefaultFiles, setAgreementDefaultFiles] = useState<
    AgreementFileValue[] | undefined
  >(() => getDefaultAgreementFilesIfDefined(predefinedInitialValues));

  function getDefaultAgreementFilesIfDefined(
    formValues?: ServiceAgreementFormValues,
  ) {
    return !isNil(formValues)
      ? getAgreementDefaultFiles(formValues)
      : undefined;
  }

  const getInitialValues = useCallback((): ServiceAgreementFormValues => {
    const initialValues: ServiceAgreementFormValues = {
      serviceAgreement: ServiceAgreementTypeEnum.MyBasePayAgreement,
      clientAgreement: [],
      memberAgreement: [],
      myBasePayAgreement: [],
      isAgreementNotAvailableConfirmed: undefined,
      contractSignerFirstName: '',
      contractSignerLastName: '',
      contractSignerPhone: '',
      contractSignerEmail: '',
      isContractSignerCompleted: undefined,
    };

    if (!isNil(predefinedInitialValues)) {
      return {
        ...predefinedInitialValues,
        isAgreementNotAvailableConfirmed:
          predefinedInitialValues.serviceAgreement ===
          ServiceAgreementTypeEnum.NotAvailable
            ? true
            : undefined,
      };
    }

    return initialValues;
  }, [predefinedInitialValues]);

  const preInitialValues = getInitialValues();

  function getAgreementDefaultFiles(values: ServiceAgreementFormValues) {
    switch (values.serviceAgreement) {
      case ServiceAgreementTypeEnum.ClientAgreement:
        return values.clientAgreement;
      case ServiceAgreementTypeEnum.MyBasePayAgreement:
        return values.myBasePayAgreement;
      case ServiceAgreementTypeEnum.MemberAgreement:
        return values.memberAgreement;
      default:
        return;
    }
  }

  const getAgreementDetailsDescription = (
    formik: FormikProps<ServiceAgreementFormValues>,
  ) => {
    switch (formik.values.serviceAgreement) {
      case ServiceAgreementTypeEnum.MyBasePayAgreement:
        return 'Please select this option if you will be utilizing the agreement template myBasePay provides to initiate a staffing arrangement.';

      case ServiceAgreementTypeEnum.ClientAgreement:
        return 'Please select this option and upload accordingly, if the customer is requiring their specific staffing/vendor contract to review and finalize.';

      case ServiceAgreementTypeEnum.MemberAgreement:
        return 'Please select this option and upload accordingly, if your organization has it’s own contract template to submit to the customer and myBasePay to initiate a staffing arrangement.';

      case ServiceAgreementTypeEnum.NotAvailable:
        return 'Prior to any Assignment start, a valid agreement must be uploaded for this Customer. A duly executed customer agreement is needed for MBP to assume liability for Worker Assignments.';
    }
  };

  const handleServiceAgreementChange = (
    event: RadioChangeEvent,
    formik: FormikProps<ServiceAgreementFormValues>,
  ) => {
    const serviceAgreementValues = event.target.value;
    const files = getDefaultAgreementFilesIfDefined({
      ...formik.values,
      serviceAgreement: serviceAgreementValues,
    });

    setAgreementDefaultFiles(files);

    formik.setFieldValue('serviceAgreement', serviceAgreementValues, false);
    formik.setFieldTouched('serviceAgreement', true, false);
  };

  return (
    <Formik
      enableReinitialize
      innerRef={formRef}
      initialValues={preInitialValues}
      validationSchema={submitValidationSchema}
      onSubmit={() => {}}
    >
      {(formik) => {
        const isContractSignerSectionVisible = isContractSignerSectionActive(
          formik.values,
        );
        const isContractSignerReadOnly =
          !isAdmin && !!formik.values.isContractSignerCompleted;

        return (
          <>
            <FormikEffect onChange={onChange} />

          <Form layout="vertical" className={style.form}>
            <Row gutter={40} align="middle">
              <Col xs={24} md={5}>
                <div className="modal-title">Agreement details</div>
              </Col>

              <Col xs={24} md={19}>
                <FormItem name="serviceAgreement">
                  <Radio.Group
                    name="serviceAgreement"
                    className="radio-tab-view"
                    onChange={(e) => handleServiceAgreementChange(e, formik)}
                  >
                    <Radio.Button
                      value={ServiceAgreementTypeEnum.MyBasePayAgreement}
                      data-id="newCustomerServiceAgreementMyBasePayAgreement"
                    >
                      myBasePay agreement
                    </Radio.Button>
                    <Radio.Button
                      value={ServiceAgreementTypeEnum.ClientAgreement}
                      data-id="newCustomerServiceAgreementClientAgreement"
                    >
                      Customer agreement
                    </Radio.Button>
                    <Radio.Button
                      value={ServiceAgreementTypeEnum.MemberAgreement}
                      data-id="newCustomerServiceAgreementMemberAgreement"
                    >
                      Member agreement
                    </Radio.Button>
                    <Radio.Button
                      value={ServiceAgreementTypeEnum.NotAvailable}
                      data-id="newCustomerServiceAgreementNotAvailable"
                    >
                      Not available
                    </Radio.Button>
                  </Radio.Group>
                </FormItem>
              </Col>
            </Row>

            <p>{getAgreementDetailsDescription(formik)}</p>

            {isFileUploaderRequired(formik.values.serviceAgreement) && (
              <>
                <div className="modal-title">Upload agreement *</div>

                <Row gutter={[0, 20]}>
                  <Col>
                    <div className="upload-wrapper">
                      <FormItem
                        name={getAgreementFieldName(
                          formik.values.serviceAgreement,
                        )}
                        showValidateError={false}
                      >
                        <AgreementUploader
                          key={formik.values.serviceAgreement}
                          isAdmin={isAdmin}
                          fieldName={getAgreementFieldName(
                            formik.values.serviceAgreement,
                          )}
                          files={agreementDefaultFiles}
                        />
                      </FormItem>
                    </div>
                  </Col>
                </Row>
              </>
            )}

            {isContractSignerSectionVisible && (
              <>
                <div className="modal-title">Customer Contract Signer</div>

                <Row gutter={40}>
                  <Col xs={24} md={12}>
                    <FormItem
                      name="contractSignerFirstName"
                      label="First name *"
                      data-id="newCustomerContractSignerFirstNameField"
                    >
                      <Input
                        name="contractSignerFirstName"
                        placeholder="Enter first name"
                        autoComplete="off"
                        disabled={isContractSignerReadOnly}
                      />
                    </FormItem>
                  </Col>

                  <Col xs={24} md={12}>
                    <FormItem
                      name="contractSignerLastName"
                      label="Last name *"
                      data-id="newCustomerContractSignerLastNameField"
                    >
                      <Input
                        name="contractSignerLastName"
                        placeholder="Enter last name"
                        autoComplete="off"
                        disabled={isContractSignerReadOnly}
                      />
                    </FormItem>
                  </Col>

                  <Col xs={24} md={12}>
                    <FormItem
                      name="contractSignerPhone"
                      label="Phone *"
                      data-id="newCustomerContractSignerPhoneField"
                    >
                      <Input
                        name="contractSignerPhone"
                        placeholder="Enter phone"
                        autoComplete="off"
                        disabled={isContractSignerReadOnly}
                      />
                    </FormItem>
                  </Col>

                  <Col xs={24} md={12}>
                    <FormItem
                      name="contractSignerEmail"
                      label="Email *"
                      data-id="newCustomerContractSignerEmailField"
                    >
                      <Input
                        name="contractSignerEmail"
                        placeholder="Enter email"
                        autoComplete="off"
                        disabled={isContractSignerReadOnly}
                      />
                    </FormItem>
                  </Col>
                </Row>
              </>
            )}

            {formik.values.serviceAgreement ===
              ServiceAgreementTypeEnum.NotAvailable && (
              <>
                <Row gutter={[0, 20]}>
                  <Col>
                    <FormItem
                      name="isAgreementNotAvailableConfirmed"
                      className="checkbox-wrapper"
                    >
                      <Checkbox name="isAgreementNotAvailableConfirmed">
                        I accept
                      </Checkbox>
                    </FormItem>
                  </Col>
                </Row>
              </>
            )}
          </Form>
          </>
        );
      }}
    </Formik>
  );
}
